import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, XCircle, Circle, HelpCircle, FileText, ChevronDown, Loader2, Inbox, Brain,
} from "lucide-react";
import { Header } from "@/components/Header";
import { getHistory } from "@/lib/api";

function StatusIcon({ status }) {
  if (status === "correct") return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
  if (status === "wrong") return <XCircle className="h-4 w-4 text-red-500" />;
  if (status === "unknown") return <HelpCircle className="h-4 w-4 text-amber-500" />;
  return <Circle className="h-4 w-4 text-slate-300 dark:text-slate-600" />;
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3">
      <div className={`font-display text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</div>
    </div>
  );
}

export default function HistoryPage({ dark, toggleTheme }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});

  useEffect(() => {
    getHistory().then(setData).catch(() => setData({ decks: [], totals: {} }));
  }, []);

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("de-DE", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <>
      <Header dark={dark} toggleTheme={toggleTheme} />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">
          Verlauf
        </h1>
        <p className="mt-2 text-slate-600 dark:text-slate-400">
          Alle hochgeladenen Kartensätze mit Datum – und dein Lernstand pro Karte.
        </p>

        {!data ? (
          <div className="grid place-items-center py-24 text-slate-400"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : data.decks.length === 0 ? (
          <div className="mt-12 text-center">
            <Inbox className="h-10 w-10 mx-auto text-slate-300 dark:text-slate-600" />
            <p className="mt-4 text-slate-600 dark:text-slate-300">Noch nichts hochgeladen.</p>
            <Link to="/" className="mt-4 inline-block rounded-xl bg-[#0F2942] dark:bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white">
              Dokument hochladen
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-6 gap-3" data-testid="history-totals">
              <Stat label="Sätze" value={data.totals.decks} color="text-slate-900 dark:text-slate-100" />
              <Stat label="Karten" value={data.totals.cards} color="text-slate-900 dark:text-slate-100" />
              <Stat label="Richtig" value={data.totals.correct} color="text-emerald-500" />
              <Stat label="Falsch" value={data.totals.wrong} color="text-red-500" />
              <Stat label="Ungewusst" value={data.totals.unknown} color="text-amber-500" />
              <Stat label="Offen" value={data.totals.unanswered} color="text-slate-400" />
            </div>

            <div className="mt-8 space-y-3">
              {data.decks.map((d) => (
                <div key={d.id} data-testid={`history-deck-${d.id}`} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
                  <button
                    data-testid={`history-deck-toggle-${d.id}`}
                    onClick={() => setOpen((o) => ({ ...o, [d.id]: !o[d.id] }))}
                    className="w-full flex items-center gap-4 p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <span className="grid place-items-center h-10 w-10 rounded-lg bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 shrink-0">
                      <FileText className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 dark:text-slate-100 truncate">{d.filename}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">{fmt(d.created_at)} · {d.card_count} Karten</div>
                    </div>
                    <div className="flex items-center gap-3 text-sm shrink-0">
                      <span className="inline-flex items-center gap-1 text-emerald-500"><CheckCircle2 className="h-4 w-4" />{d.correct}</span>
                      <span className="inline-flex items-center gap-1 text-red-500"><XCircle className="h-4 w-4" />{d.wrong}</span>
                      <span className="inline-flex items-center gap-1 text-amber-500"><HelpCircle className="h-4 w-4" />{d.unknown}</span>
                      <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open[d.id] ? "rotate-180" : ""}`} />
                    </div>
                  </button>
                  {open[d.id] && (
                    <div className="border-t border-slate-100 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
                      {d.cards.map((c) => (
                        <div key={c.id} className="flex items-center gap-3 px-4 py-2.5">
                          <StatusIcon status={c.status} />
                          <span className="text-xs font-mono text-slate-400 w-14 shrink-0">Karte {c.number}</span>
                          <span className="text-sm text-slate-700 dark:text-slate-300 truncate">{c.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <Link to="/quiz" data-testid="history-start-quiz" className="inline-flex items-center gap-2 rounded-xl bg-[#0F2942] dark:bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-[#1E3A5F] dark:hover:bg-blue-500">
                <Brain className="h-4 w-4" /> Quiz starten
              </Link>
            </div>
          </>
        )}
      </main>
    </>
  );
}
